import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Checkbox,
  Chip,
  Collapse,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckIcon from '@mui/icons-material/Check';
import {
  CLASS_MODULE_LEVELS,
  DEFAULT_CLASS_MODULE_LEVEL,
  normalizeModuleLevel,
} from '../../constants/classLogModuleLevels';
import {
  buildClassLogPayloadV2,
  buildNextPlanPayloadV2,
  checkedTypesFromLog,
  emptyClassLogJson,
  emptyNextPlanJson,
  moduleFieldsFromLog,
  parseClassLogForForm,
  parseNextPlanForForm,
  getLmsModulesMissingActivities,
  LMS_ASSIGNMENT_REQUIRED_MESSAGE,
  LMS_ASSIGNMENT_REQUIRED_PLAN_MESSAGE,
} from '../../utils/classSessionLog';
import { isMandatoryNextPlanModuleName } from '../../utils/mandatoryNextPlanModules';

const emptyLmsFields = () => ({ level: DEFAULT_CLASS_MODULE_LEVEL, activityIds: [] });

function LastPlanSuggestionAlert({ suggestion, selectedCount, miscRemarks }) {
  if (!suggestion) return null;
  const dateBit = suggestion.fromDateLabel ? ` (${suggestion.fromDateLabel})` : '';
  const cleared = suggestion.edited && selectedCount === 0 && !String(miscRemarks || '').trim();
  return (
    <Alert severity="warning" sx={{ mb: 1.5 }}>
      <Typography variant="body2" fontWeight={800}>
        Filled in from your last class plan — not saved yet
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.5 }}>
        We ticked what you planned after the last class{dateBit}. Check that this is what you actually covered today. Change anything that was different, then press Save Class Log.
      </Typography>
      {suggestion.edited ? (
        <Typography variant="body2" fontWeight={700} sx={{ mt: 0.75 }}>
          {cleared
            ? 'You cleared these. Tick what you covered today, then press Save Class Log.'
            : 'You changed these. Press Save Class Log to keep them for today.'}
        </Typography>
      ) : null}
    </Alert>
  );
}

function LevelSelect({ value, onChange, disabled, label = 'Level', sx }) {
  return (
    <FormControl size="small" sx={{ minWidth: { sm: 140 }, maxWidth: { sm: 180 }, ...sx }}>
      <InputLabel>{label}</InputLabel>
      <Select
        label={label}
        value={normalizeModuleLevel(value)}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      >
        {CLASS_MODULE_LEVELS.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function ModuleCheckboxGrid({ checkedTypes, onToggle, disabled, idPrefix, modules = [], lockedTypes }) {
  const locked = lockedTypes || new Set();
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' },
        gap: 0.5,
      }}
    >
      {(modules || []).map((mod) => {
        const isLocked = locked.has(mod.type) || locked.has(String(mod.type));
        return (
          <FormControlLabel
            key={`${idPrefix}-${mod.type}`}
            control={
              <Checkbox
                size="small"
                checked={checkedTypes.has(mod.type) || isLocked}
                onClick={(e) => {
                  if (isLocked) e.preventDefault();
                }}
                onChange={() => {
                  if (isLocked) return;
                  onToggle(mod.type);
                }}
                disabled={disabled}
                inputProps={isLocked ? { 'aria-readonly': true } : undefined}
              />
            }
            label={
              <Box>
                <Typography variant="body2" fontWeight={700}>
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                    {mod.label}
                  </Box>
                  <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                    {mod.shortLabel || mod.label}
                  </Box>
                </Typography>
                {isLocked ? (
                  <Typography variant="caption" color="text.secondary" fontWeight={700} display="block">
                    Required
                  </Typography>
                ) : null}
              </Box>
            }
            sx={{ m: 0, alignItems: 'flex-start' }}
          />
        );
      })}
    </Box>
  );
}

function LmsModuleFields({
  moduleId,
  label,
  fields,
  onLevelChange,
  onToggleActivity,
  disabled,
  activities,
  showActivityError = false,
}) {
  const selectedIds = new Set(fields.activityIds || []);
  const selectedCount = selectedIds.size;

  return (
    <Box
      sx={{
        pl: { xs: 0, sm: 4 },
        py: 1,
        borderLeft: { xs: 'none', sm: '3px solid' },
        borderColor: { xs: 'transparent', sm: 'primary.light' },
        bgcolor: { xs: 'rgba(0,0,0,0.02)', sm: 'transparent' },
        borderRadius: 2,
        px: { xs: 1.5, sm: 0 },
      }}
    >
      <Typography variant="caption" fontWeight={800} color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {label}
      </Typography>
      <LevelSelect
        value={fields.level}
        onChange={(level) => onLevelChange(moduleId, level)}
        disabled={disabled}
        label="Level"
        sx={{ mb: 1.5 }}
      />
      <Typography variant="body2" fontWeight={700} sx={{ mb: 0.75 }}>
        Activities covered (tap all that apply)
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 0.75 }}>
        {(activities || []).map((a) => {
          const selected = selectedIds.has(a.activityId);
          return (
            <Chip
              key={a.activityId}
              label={a.activityName}
              clickable={!disabled}
              disabled={disabled}
              onClick={() => onToggleActivity(moduleId, a.activityId)}
              color={selected ? 'primary' : 'default'}
              variant={selected ? 'filled' : 'outlined'}
              icon={selected ? <CheckIcon /> : undefined}
              sx={{
                fontWeight: 700,
                height: 'auto',
                py: 0.75,
                '& .MuiChip-label': { whiteSpace: 'normal', lineHeight: 1.3 },
              }}
            />
          );
        })}
      </Box>
      <Typography variant="caption" color={showActivityError ? 'error.main' : 'text.secondary'} fontWeight={600}>
        {showActivityError
          ? 'Pick at least one activity for this module.'
          : `${selectedCount} activit${selectedCount === 1 ? 'y' : 'ies'} selected`}
      </Typography>
    </Box>
  );
}

export default function ClassLogForm({
  classLogJson,
  nextPlanJson,
  onClassLogChange,
  onNextPlanChange,
  disabled = false,
  showNextPlan = true,
  showValidationHint = false,
  showPlanValidationHint = false,
  section = 'both',
  lmsOptions = null,
  legacyClassLogPreview = null,
  legacyNextPlanPreview = null,
  nextPlanRequired = false,
  lastPlanSuggestion = null,
}) {
  const hasLmsOptions = !!(lmsOptions?.modules?.length);
  const useLmsClass = hasLmsOptions;
  const useLmsPlan = hasLmsOptions;
  const formClassLog = useMemo(() => parseClassLogForForm(classLogJson), [classLogJson]);
  const formNextPlan = useMemo(() => parseNextPlanForForm(nextPlanJson), [nextPlanJson]);

  const [classChecked, setClassChecked] = useState(() => checkedTypesFromLog(formClassLog.coveredModules));
  const [classFields, setClassFields] = useState(() => moduleFieldsFromLog(formClassLog.coveredModules));
  const [miscRemarks, setMiscRemarks] = useState(formClassLog.miscRemarks || '');

  const [planExpanded, setPlanExpanded] = useState(
    () => section === 'plan' || formNextPlan.plannedModules.length > 0 || !!formNextPlan.legacyText
  );
  const [planChecked, setPlanChecked] = useState(() => checkedTypesFromLog(formNextPlan.plannedModules));
  const [planFields, setPlanFields] = useState(() => moduleFieldsFromLog(formNextPlan.plannedModules));
  const [planMisc, setPlanMisc] = useState(formNextPlan.legacyText || '');

  const lmsModuleOptions = useMemo(
    () =>
      (lmsOptions?.modules || []).map((m) => ({
        type: m.moduleId,
        label: m.moduleName,
        shortLabel: m.moduleName,
        activities: m.activities || [],
      })),
    [lmsOptions]
  );
  const lockedPlanIds = useMemo(() => {
    const ids = new Set();
    lmsModuleOptions.forEach((m) => {
      if (isMandatoryNextPlanModuleName(m.label)) ids.add(String(m.type));
    });
    return ids;
  }, [lmsModuleOptions]);
  const managesPlan = section === 'plan' || section === 'both';
  const onNextPlanChangeRef = useRef(onNextPlanChange);
  onNextPlanChangeRef.current = onNextPlanChange;

  useEffect(() => {
    const next = parseClassLogForForm(classLogJson);
    setClassChecked(checkedTypesFromLog(next.coveredModules));
    setClassFields(moduleFieldsFromLog(next.coveredModules));
    setMiscRemarks(next.miscRemarks || '');
  }, [classLogJson]);

  useEffect(() => {
    const next = parseNextPlanForForm(nextPlanJson);
    const checked = checkedTypesFromLog(next.plannedModules);
    const fields = moduleFieldsFromLog(next.plannedModules);
    let added = false;
    lockedPlanIds.forEach((id) => {
      if (!checked.has(id)) {
        checked.add(id);
        added = true;
      }
      if (!fields[id]) {
        fields[id] = emptyLmsFields();
        added = true;
      }
    });
    setPlanChecked(checked);
    setPlanFields(fields);
    setPlanMisc(next.legacyText || '');
    if (next.plannedModules.length > 0 || next.legacyText || lockedPlanIds.size > 0) {
      setPlanExpanded(true);
    }
    if (added && useLmsPlan && managesPlan) {
      onNextPlanChangeRef.current?.(buildNextPlanPayloadV2(checked, fields, next.legacyText || ''));
    }
  }, [nextPlanJson, lockedPlanIds, useLmsPlan, managesPlan]);

  const emitClassLog = (checked, fields, misc) => {
    if (!useLmsClass) return;
    onClassLogChange?.(buildClassLogPayloadV2(checked, fields, misc));
  };

  const emitNextPlan = (checked, fields, misc) => {
    if (!useLmsPlan) return;
    onNextPlanChange?.(buildNextPlanPayloadV2(checked, fields, misc));
  };

  const toggleClassModule = (type) => {
    const next = new Set(classChecked);
    const nextFields = { ...classFields };
    if (next.has(type)) {
      next.delete(type);
    } else {
      next.add(type);
      if (!nextFields[type]) {
        nextFields[type] = emptyLmsFields();
      }
    }
    setClassChecked(next);
    setClassFields(nextFields);
    emitClassLog(next, nextFields, miscRemarks);
  };

  const updateClassField = (type, field, value) => {
    const nextFields = {
      ...classFields,
      [type]: {
        ...(classFields[type] || emptyLmsFields()),
        [field]: value,
      },
    };
    setClassFields(nextFields);
    emitClassLog(classChecked, nextFields, miscRemarks);
  };

  const toggleClassActivity = (moduleId, activityId) => {
    const current = new Set(classFields[moduleId]?.activityIds || []);
    if (current.has(activityId)) current.delete(activityId);
    else current.add(activityId);
    updateClassField(moduleId, 'activityIds', [...current]);
  };

  const updateMisc = (value) => {
    setMiscRemarks(value);
    emitClassLog(classChecked, classFields, value);
  };

  const togglePlanModule = (type) => {
    if (lockedPlanIds.has(String(type))) return;
    const next = new Set(planChecked);
    lockedPlanIds.forEach((id) => next.add(id));
    const nextFields = { ...planFields };
    if (next.has(type)) {
      next.delete(type);
    } else {
      next.add(type);
      if (!nextFields[type]) {
        nextFields[type] = emptyLmsFields();
      }
    }
    lockedPlanIds.forEach((id) => {
      if (!nextFields[id]) nextFields[id] = emptyLmsFields();
    });
    setPlanChecked(next);
    setPlanFields(nextFields);
    emitNextPlan(next, nextFields, planMisc);
  };

  const updatePlanField = (type, field, value) => {
    const nextFields = {
      ...planFields,
      [type]: {
        ...(planFields[type] || emptyLmsFields()),
        [field]: value,
      },
    };
    const checked = new Set(planChecked);
    lockedPlanIds.forEach((id) => {
      checked.add(id);
      if (!nextFields[id]) nextFields[id] = emptyLmsFields();
    });
    setPlanFields(nextFields);
    emitNextPlan(checked, nextFields, planMisc);
  };

  const togglePlanActivity = (moduleId, activityId) => {
    const current = new Set(planFields[moduleId]?.activityIds || []);
    if (current.has(activityId)) current.delete(activityId);
    else current.add(activityId);
    updatePlanField(moduleId, 'activityIds', [...current]);
  };

  const updatePlanMisc = (value) => {
    setPlanMisc(value);
    const checked = new Set(planChecked);
    lockedPlanIds.forEach((id) => checked.add(id));
    emitNextPlan(checked, planFields, value);
  };

  const prefillPlanFromClass = () => {
    const next = new Set(classChecked);
    lockedPlanIds.forEach((id) => next.add(id));
    const nextFields = { ...planFields };
    classChecked.forEach((type) => {
      nextFields[type] = {
        level: normalizeModuleLevel(classFields[type]?.level),
        activityIds: [...(classFields[type]?.activityIds || [])],
      };
    });
    lockedPlanIds.forEach((id) => {
      if (!nextFields[id]) nextFields[id] = emptyLmsFields();
    });
    setPlanChecked(next);
    setPlanFields(nextFields);
    setPlanExpanded(true);
    emitNextPlan(next, nextFields, planMisc);
  };

  const classModuleOptions = lmsModuleOptions;
  const planModuleOptions = lmsModuleOptions;
  const planCheckedDisplay = useMemo(() => {
    const next = new Set(planChecked);
    lockedPlanIds.forEach((id) => next.add(id));
    return next;
  }, [planChecked, lockedPlanIds]);

  const selectedCount = classChecked.size;
  const planSelectedCount = planCheckedDisplay.size;
  const classTotalModuleCount = classModuleOptions.length || 0;
  const planTotalModuleCount = planModuleOptions.length || 0;
  const classModulesMissingNotes = getLmsModulesMissingActivities(classChecked, classFields);
  const planModulesMissingNotes = getLmsModulesMissingActivities(planCheckedDisplay, planFields);
  const showClassSection = section === 'class' || section === 'both';
  const showPlanSection = (section === 'plan' || section === 'both') && showNextPlan;
  const planVisible = section === 'plan' || planExpanded;

  const classLogContent = hasLmsOptions ? (
    <>
      {showClassSection ? (
        <LastPlanSuggestionAlert
          suggestion={lastPlanSuggestion}
          selectedCount={selectedCount}
          miscRemarks={miscRemarks}
        />
      ) : null}
      {legacyClassLogPreview ? (
        <Alert severity="info" sx={{ mb: 1 }}>
          <Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 0.5 }}>
            Previous class log (legacy, read-only)
          </Typography>
          <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-wrap' }}>
            {legacyClassLogPreview}
          </Typography>
        </Alert>
      ) : null}
      <Box>
        <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1.5 }}>
          <Typography variant="body1" fontWeight={800} color="text.primary">
            What was covered today?
          </Typography>
          <Chip
            size="small"
            label={`${selectedCount}/${classTotalModuleCount || 0} selected`}
            color={selectedCount > 0 ? 'primary' : 'default'}
            variant={selectedCount > 0 ? 'filled' : 'outlined'}
            sx={{ fontWeight: 800 }}
          />
        </Stack>
        <ModuleCheckboxGrid
          checkedTypes={classChecked}
          onToggle={toggleClassModule}
          disabled={disabled}
          idPrefix="class"
          modules={classModuleOptions}
        />
      </Box>

      <Stack spacing={1.5}>
        {classModuleOptions.filter((m) => classChecked.has(m.type)).map((mod) => (
          <LmsModuleFields
            key={`class-field-${mod.type}`}
            moduleId={mod.type}
            label={mod.label}
            fields={classFields[mod.type] || emptyLmsFields()}
            onLevelChange={(id, level) => updateClassField(id, 'level', level)}
            onToggleActivity={toggleClassActivity}
            disabled={disabled}
            activities={mod.activities || []}
            showActivityError={showValidationHint && classModulesMissingNotes.includes(mod.type)}
          />
        ))}
      </Stack>

      <TextField
        label="Anything else about today's class? (optional)"
        value={miscRemarks}
        onChange={(e) => updateMisc(e.target.value)}
        disabled={disabled}
        multiline
        minRows={2}
        fullWidth
        placeholder="e.g. started late, guest teacher, special focus…"
        inputProps={{ maxLength: 2000 }}
      />

      {showValidationHint && classModulesMissingNotes.length > 0 ? (
        <Alert severity="warning" sx={{ py: 0.5 }}>
          Pick at least one activity for each selected module.
        </Alert>
      ) : null}
      {showValidationHint && selectedCount === 0 && !String(miscRemarks || '').trim() ? (
        <Alert severity="warning" sx={{ py: 0.5 }}>
          Select at least one module with activities or add other notes to save the class log.
        </Alert>
      ) : null}
    </>
  ) : (
    <Alert severity="info">{LMS_ASSIGNMENT_REQUIRED_MESSAGE}</Alert>
  );

  const planContent = hasLmsOptions ? (
    <Box>
      {section !== 'plan' ? (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ cursor: disabled ? 'default' : 'pointer', userSelect: 'none', mb: planVisible ? 1.5 : 0 }}
          onClick={() => {
            if (!disabled) setPlanExpanded((v) => !v);
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <ExpandMoreIcon
              fontSize="small"
              sx={{
                transform: planVisible ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s',
              }}
            />
            <Typography variant="body1" fontWeight={800}>
              Plan next class?
            </Typography>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              {nextPlanRequired ? '(required)' : '(optional)'}
            </Typography>
          </Stack>
          {!planVisible && classChecked.size > 0 && !disabled ? (
            <Typography
              variant="caption"
              color="primary"
              fontWeight={700}
              onClick={(e) => {
                e.stopPropagation();
                prefillPlanFromClass();
              }}
              sx={{ cursor: 'pointer' }}
            >
              Prefill from today
            </Typography>
          ) : null}
        </Stack>
      ) : (
        <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1.5 }}>
          <Typography variant="body1" fontWeight={800} color="text.primary">
            What to cover in the next class?
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              size="small"
              label={`${planSelectedCount}/${planTotalModuleCount || 0} selected`}
              color={planSelectedCount > 0 ? 'info' : 'default'}
              variant={planSelectedCount > 0 ? 'filled' : 'outlined'}
              sx={{ fontWeight: 800 }}
            />
            {classChecked.size > 0 && !disabled ? (
              <Typography
                variant="caption"
                color="primary"
                fontWeight={700}
                onClick={prefillPlanFromClass}
                sx={{ cursor: 'pointer' }}
              >
                Prefill from today
              </Typography>
            ) : null}
          </Stack>
        </Stack>
      )}

      <Collapse in={planVisible}>
        <Box sx={{ pt: section === 'plan' ? 0 : 0.5 }}>
          {legacyNextPlanPreview ? (
            <Alert severity="info" sx={{ mb: 1.5 }}>
              <Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 0.5 }}>
                Previous plan (legacy, read-only)
              </Typography>
              <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-wrap' }}>
                {legacyNextPlanPreview}
              </Typography>
            </Alert>
          ) : null}

          <ModuleCheckboxGrid
            checkedTypes={planCheckedDisplay}
            onToggle={togglePlanModule}
            disabled={disabled}
            idPrefix="plan"
            modules={planModuleOptions}
            lockedTypes={lockedPlanIds}
          />
          {lockedPlanIds.size > 0 ? (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              Required modules stay selected. Pick at least one activity for each before saving.
            </Typography>
          ) : null}

          <Stack spacing={1.5} sx={{ mt: 1.5 }}>
            {planModuleOptions.filter((m) => planCheckedDisplay.has(String(m.type)) || planCheckedDisplay.has(m.type)).map((mod) => (
              <LmsModuleFields
                key={`plan-field-${mod.type}`}
                moduleId={mod.type}
                label={mod.label}
                fields={planFields[mod.type] || emptyLmsFields()}
                onLevelChange={(id, level) => updatePlanField(id, 'level', level)}
                onToggleActivity={togglePlanActivity}
                disabled={disabled}
                activities={mod.activities || []}
                showActivityError={showPlanValidationHint && planModulesMissingNotes.includes(mod.type)}
              />
            ))}
          </Stack>

          <TextField
            label="Anything else for next class? (optional)"
            value={planMisc}
            onChange={(e) => updatePlanMisc(e.target.value)}
            disabled={disabled}
            multiline
            minRows={2}
            fullWidth
            sx={{ mt: 1.5 }}
            placeholder="e.g. prepare worksheets, parent demo…"
            inputProps={{ maxLength: 8000 }}
          />

          {showPlanValidationHint && planModulesMissingNotes.length > 0 ? (
            <Alert severity="warning" sx={{ py: 0.5, mt: 1.5 }}>
              Pick at least one activity for each selected module in the plan.
            </Alert>
          ) : null}
        </Box>
      </Collapse>
    </Box>
  ) : (
    <Alert severity="info">{LMS_ASSIGNMENT_REQUIRED_PLAN_MESSAGE}</Alert>
  );

  if (section === 'class') {
    return <Stack spacing={2.5}>{classLogContent}</Stack>;
  }

  if (section === 'plan') {
    return <Stack spacing={2}>{planContent}</Stack>;
  }

  return (
    <Stack spacing={3}>
      {showClassSection ? classLogContent : null}
      {showPlanSection ? planContent : null}
    </Stack>
  );
}

export { emptyClassLogJson, emptyNextPlanJson };
